import * as actions from '../../action';
import {
  createGroupAction,
  exportFrameImage,
  importTemplate,
  isFrameReady,
  unGroupAction,
} from '../../action';
import { removeHideProperties } from '../../lib/clipboard';
import { blobToDataURL } from '../../common-utils';
import { createId } from '../../render/utils/node';
import { templateCopy } from '../utils/node';
import { TYPES } from '../constants';
import { RenderContext, UnattachedRenderContext } from './createRenderContext';
import type { NodeLike } from './node';

async function exportState(this: RenderContext, { dropMessages = false } = {}) {
  const data = await exportTemplate.call(this, [null, { allFrame: true, keepSrc: true }]);
  if (dropMessages) {
    data.messages = [];
  }
  return data;
}

async function exportTemplate(
  this: RenderContext,
  frameId,
  { allFrame = false, keepSrc = false } = {}
) {
  const card = templateCopy(this.card);
  const assets = {};
  const nodes: Record<string, NodeLike> = {};

  const topNodes = this.api.getFrames();
  frameId = frameId || topNodes[0].id;
  const targetFrame = topNodes.find((n) => n.id === frameId);
  if (!targetFrame) return null;
  let targetNodes: NodeLike[] = [];

  if (allFrame) {
    targetNodes = [...topNodes];
  } else {
    targetNodes = [targetFrame];
  }

  const descendants = targetNodes.map((n) => this.api.getDescendants(n.id)).flat();

  const allNodes = [...targetNodes, ...descendants];
  allNodes.map((n) => {
    nodes[n.id] = removeHideProperties(n);
  });

  if (!keepSrc) {
    await Promise.all(
      Object.values(nodes)
        .filter((node) => node.type === TYPES.image)
        .map(async (node: NodeLike) => {
          if (node.src) {
            const url = await this.providers.asset.getAsset(node.src);
            const blob = await fetch(url).then((res) => res.blob());
            const dataUrl = await blobToDataURL(blob);
            const extension = blob.type === 'image/svg+xml' ? 'svg' : blob.type.split('/')[1];
            const id = createId();
            assets[id] = {
              id: id,
              meta: {
                filename: `${id}.${extension}`,
              },
              url: dataUrl,
            };
            node.src = `asset://${id}`;
          }
        })
    );
  }
  card.assets = assets;
  card.props.nodes = nodes;
  return card;
}

function exportImage(this: RenderContext, options) {
  return exportFrameImage(this, options);
}

function exportFrameIsReady(this: RenderContext) {
  return isFrameReady(this);
}

function downloadWithBlob(name: string, blob: Blob | string, ext: string) {
  name = `${name || 'seede'}.${ext}`;
  if (!blob) return;

  const url = typeof blob === 'string' ? blob : URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const extContentTypes = {
  png: 'image/png',
  jpg: 'image/jpeg',
  webp: 'image/webp',
};

async function downloadImage(this: RenderContext, ext = 'png', payload = { name: '', dpr: 1 }) {
  const contentType = extContentTypes[ext] || extContentTypes.png;
  const img = await this.api.exportImage({ ...payload, contentType });
  const blob = await fetch(img).then((res) => res.blob());
  downloadWithBlob(payload.name, blob, ext);
}

export function extendAPI(renderContext: RenderContext | UnattachedRenderContext) {
  const ctx = renderContext as RenderContext;
  Object.assign(renderContext.api, {
    exportState: exportState.bind(ctx),
    exportTemplate: exportTemplate.bind(ctx),
    exportFrameIsReady: exportFrameIsReady.bind(ctx),
    exportImage: exportImage.bind(ctx),
    downloadImage: downloadImage.bind(ctx),
    importTemplate: (template, frame) => importTemplate(renderContext, template, frame),
    groupAction: () => createGroupAction.handler(renderContext),
    unGroupAction: () => unGroupAction.handler(renderContext),
    addText: (payload) => actions.addText(renderContext, payload),
    addShape: (payload) => actions.addShape(renderContext, payload),
    addImage: (payload) => actions.addImage(renderContext, payload),
  });
}
